/* eslint-disable react/no-array-index-key */
import React, { useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useFormik } from "formik";
import * as yup from "yup";

import { Button, Input } from "@components/Form";
import { BoldClose } from "@assets/icons";
import { setFieldForm } from "@utils/helpers";
import { CustomModal } from "@components/Modals";
import { Divider } from "@components/Static";
import Dropdown from "@components/Form/Dropdown";

const FORM_STATE = {
  FILTERS: "filters",
};

const UserFilterModal = ({
  isOpened,
  onSubmit,
  onToggle,
  filterOptions,
  filters,
  isShowInfoStrip = false,
  infoStripContent = "",
}) => {
  const { t } = useTranslation();

  const equalityOptions = [
    {
      value: "true",
      label: t("filterModal.is"),
    },
    {
      value: "false",
      label: t("filterModal.isNot"),
    },
  ];

  const validationSchema = yup.object({
    [FORM_STATE.FILTERS]: yup.array().of(
      // Deleting the last remaining filter row resets it to `{}` (a
      // placeholder so the modal isn't left with zero rows) rather than
      // truly emptying the array — and `handleAddCondition` adds a fresh
      // `{}` row the same way. `handleFormSubmit` already treats a
      // completely untouched row as "no filter here" and strips it before
      // submitting; validation needs that same exemption; otherwise an
      // empty placeholder row blocks Save entirely, including the "remove
      // my only filter" flow.
      yup.lazy((row) =>
        row && Object.keys(row).length
          ? yup.object().shape({
              key: yup.string().max(50).required(t("common.requiredField")),
              isEqual: yup.boolean(),
              value: yup.mixed().when("key", ([key]) => {
                // Yup v1's `.when(dep, callback)` always passes the dependency
                // values as an array (`[key]`), even for a single string
                // dependency — passing the bare `key` here meant this never
                // matched any filterOption, so the numeric branch below never
                // actually ran for any field, including Age.
                const option = filterOptions?.find((v) => v.value === key);

                if (option?.isNumeric) {
                  // If key is numeric, enforce number validation. Yup's own
                  // number-cast turns a blank string into NaN, but NaN isn't
                  // `null`/`undefined`, so a bare `.required()` doesn't catch an
                  // empty field — it would silently pass through and later get
                  // coerced to 0 in handleFormSubmit. Transforming blank input to
                  // `undefined` first makes `.required()` correctly flag it.
                  return yup
                    .number()
                    .transform((value, originalValue) =>
                      typeof originalValue === "string" &&
                      originalValue.trim() === ""
                        ? undefined
                        : value
                    )
                    .required(t("common.requiredField"));
                } else {
                  // Otherwise, treat as a string with max length
                  return yup.string().trim().max(50);
                }
              }),
            })
          : yup.mixed().notRequired()
      )
    ),
  });

  const initialState = useMemo(
    () => ({ [FORM_STATE.FILTERS]: filters?.length ? filters : [{}] }),
    [filters]
  );

  const { handleSubmit, setFieldValue, values, errors, setErrors, resetForm } =
    useFormik({
      initialValues: initialState,
      validationSchema,
      enableReinitialize: true,
      onSubmit: (val) => handleFormSubmit(val),
    });

  useEffect(() => {
    if (!isOpened) {
      resetForm();
    }
  }, [isOpened]);

  const handleAddCondition = () => {
    setFieldForm(setFieldValue, setErrors, FORM_STATE.FILTERS, [
      ...values[FORM_STATE.FILTERS],
      {},
    ]);
  };

  const handleFormSubmit = (formValues) => {
    const result = formValues[FORM_STATE.FILTERS]
      .filter((v) => !!Object.keys(v).length)
      .map((v) => {
        const optionValue = filterOptions.find((j) => j.value === v.key);
        if (!optionValue) {
          return {};
        }

        let { value } = v;
        if (optionValue.isNumeric) {
          // A blank value must stay unset, not silently become 0 — form
          // validation above already blocks submitting a blank numeric
          // filter, but this guards against ever sending a fabricated "is 0"
          // condition the user never entered.
          const isBlank =
            v.value === undefined ||
            v.value === null ||
            String(v.value).trim() === "";
          const numericValue = Number(v.value);
          value = isBlank || Number.isNaN(numericValue) ? undefined : numericValue;
        }

        return {
          key: v.key,
          isEqual: !!v.isEqual,
          value,
        };
      });

    onSubmit(result);
    onToggle(false);
  };

  const handleFilterDelete = (index) => {
    const filter = values[FORM_STATE.FILTERS][index];

    if (!filter) {
      return;
    }

    if (values[FORM_STATE.FILTERS].length !== 1) {
      setFieldForm(
        setFieldValue,
        setErrors,
        FORM_STATE.FILTERS,
        values[FORM_STATE.FILTERS].filter((__v, i) => i !== index)
      );
      return;
    }

    if (!filter.key) {
      return;
    }

    setFieldForm(setFieldValue, setErrors, FORM_STATE.FILTERS, [{}]);
  };

  const keyBasedDropdowns = (filter, index) => {
    const optionValue = filterOptions.find((v) => v.value === filter.key);

    return (
      <div key={`filter-${index}`} className="flex flex-row mt-5">
        <Dropdown
          onChange={({ value }) => {
            setFieldForm(
              setFieldValue,
              setErrors,
              `${FORM_STATE.FILTERS}[${index}][value]`,
              undefined
            );
            setFieldForm(
              setFieldValue,
              setErrors,
              `${FORM_STATE.FILTERS}[${index}][key]`,
              value
            );
          }}
          scrollID={`filter-${index}-key`}
          classSelect="h-full"
          options={filterOptions}
          value={filter.key}
          className="bg-silver-light flex rounded cursor-pointer w-64 h-14"
        />
        <Dropdown
          onChange={({ value }) =>
            setFieldForm(
              setFieldValue,
              setErrors,
              `${FORM_STATE.FILTERS}[${index}][isEqual]`,
              value === "true"
            )
          }
          scrollID={`filter-${index}-equality`}
          classSelect="h-full"
          options={equalityOptions}
          value={filter.isEqual ? "true" : "false"}
          className="bg-silver-light flex rounded cursor-pointer w-32 h-14 ml-6"
        />
        {optionValue?.options ? (
          <Dropdown
            onChange={({ value }) =>
              setFieldForm(
                setFieldValue,
                setErrors,
                `${FORM_STATE.FILTERS}[${index}][value]`,
                value
              )
            }
            scrollID={`filter-${index}-value`}
            classSelect="h-full"
            options={optionValue.options}
            value={filter.value}
            error={
              errors[FORM_STATE.FILTERS] &&
              errors[FORM_STATE.FILTERS][index]?.value
            }
            className="bg-silver-light flex rounded cursor-pointer flex-1 h-14 ml-6"
          />
        ) : (
          <Input
            className="flex flex-1 ml-6"
            innerClassName="w-full"
            error={
              errors[FORM_STATE.FILTERS] &&
              errors[FORM_STATE.FILTERS][index]?.value
            }
            messageClassName="top-full mt-1 whitespace-nowrap"
            placeholder={t("filterModal.conditionValue")}
            value={filter.value ?? ""}
            onChange={(value) => {
              if (optionValue.isNumeric && !Number.isInteger(Number(value))) {
                return;
              }

              setFieldForm(
                setFieldValue,
                setErrors,
                `${FORM_STATE.FILTERS}[${index}][value]`,
                value
              );
            }}
          />
        )}
        <BoldClose
          onClick={() => handleFilterDelete(index)}
          className="ml-6 cursor-pointer self-center hover:opacity-80"
        />
      </div>
    );
  };

  return (
    <CustomModal
      outsideClickClose
      hideCloseButton
      openModal={isOpened}
      setOpenModal={onToggle}
      className="bg-milk rounded-xl w-130 h-125"
    >
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        <div className="flex items-center">
          <span className="flex items-center font-bold-poppins text-3xl text-dawn-gray tracking-tight py-4 px-6 md:py-5 md:px-8 lg:py-6 lg:px-10 leading-10">
            {t("filterModal.addFilter")}
            {isShowInfoStrip ? (
              <span className="font-light-poppins text-dawn-gray text-lg pl-4 tracking-tight ">
                ( {infoStripContent} )
              </span>
            ) : null}
          </span>
        </div>
        <Divider />
        <div className="flex flex-col flex-1 px-10 py-4 overflow-y-auto">
          <span className="text-dawn-gray text-lg mb-2">
            {t("filterModal.description")}
          </span>
          {values[FORM_STATE.FILTERS].map((filter, index) => {
            if (!filter?.key) {
              return (
                <div key={`filter-${index}`} className="flex flex-row mt-5">
                  <Dropdown
                    onChange={({ value }) =>
                      setFieldForm(
                        setFieldValue,
                        setErrors,
                        `${FORM_STATE.FILTERS}[${index}][key]`,
                        value
                      )
                    }
                    scrollID={`filter-${index}`}
                    key={`filter-${index}`}
                    placeholder={t("filterModal.chooseFilter")}
                    classSelect=" h-full"
                    options={filterOptions}
                    error={
                      errors[FORM_STATE.FILTERS] &&
                      errors[FORM_STATE.FILTERS][index]?.key
                    }
                    className="bg-silver-light flex rounded cursor-pointer w-64 h-14"
                  />
                  {values[FORM_STATE.FILTERS].length > 1 && (
                    <BoldClose
                      width={50}
                      onClick={() => handleFilterDelete(index)}
                      className="ml-6 cursor-pointer self-center hover:opacity-80"
                    />
                  )}
                </div>
              );
            }

            return keyBasedDropdowns(filter, index);
          })}
          <div className="mt-5">
            <span
              onClick={() => {
                if (values[FORM_STATE.FILTERS].length < 40) {
                  handleAddCondition();
                }
              }}
              className={clsx(
                "font-medium-poppins text-lg",
                values[FORM_STATE.FILTERS].length < 40
                  ? "text-primary cursor-pointer hover:opacity-80"
                  : "text-silver"
              )}
            >
              {t("filterModal.addCondition")}
            </span>
          </div>
        </div>
        <div className="flex flex-row gap-1 justify-end shadow-top py-4 px-6 md:py-5 md:px-8 lg:py-6 lg:px-10">
          <Button
            className="py-3 px-16 rounded-full"
            variant="negative"
            onClick={() => onToggle(false)}
          >
            <span className="text-primary text-base">{t("common.cancel")}</span>
          </Button>
          <Button type="submit" className="py-3 px-16 rounded-full">
            <span className="text-white text-base ">{t("common.save")}</span>
          </Button>
        </div>
      </form>
    </CustomModal>
  );
};

export default React.memo(UserFilterModal);
